import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { BottomNavigation, BottomNavigationAction, List, ListItem, styled } from '@mui/material'
import type { FC } from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router'

import { COLORS, ICONS, unit } from '@hcr/ui'

import { ReferenceName, useHoliday, useUserDetails } from '../../../../../contexts'
import type { Route } from '../../../../../models'
import { EMPTY_PATH_PARAM, Path } from '../../../../../models'
import { to } from '../../../../../utils'

interface NavigationItem {
  icons: {
    active: IconDefinition
    normal: IconDefinition
  }
  label: string
  route: Route
}

interface NavigationProps {
  ref?: ((node: HTMLElement | null) => void) | null
  variant: 'horizontal' | 'vertical'
}

export const Navigation: FC<NavigationProps> = ({ variant, ref }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const userDetails = useUserDetails()
  const {
    data: { destinationId },
  } = useHoliday()
  const navigate = useNavigate()

  const navigationItems = useMemo<NavigationItem[]>(
    () => [
      {
        icons: {
          active: ICONS.fasHouse,
          normal: ICONS.farHouse,
        },
        label: t('navigation.home'),
        route: Path.Home,
      },
      {
        icons: {
          active: ICONS.fasGiftCard,
          normal: ICONS.farGiftCard,
        },
        label: t('navigation.booking'),
        route: Path.Booking,
      },
      {
        icons: {
          active: ICONS.fasUtensils,
          normal: ICONS.farUtensils,
        },
        label: t('navigation.services'),
        route: [Path.Services, { destinationId: destinationId ?? EMPTY_PATH_PARAM }],
      },
      {
        icons: userDetails.data?.has_active_contracts
          ? { active: ICONS.fasBadgeCheck, normal: ICONS.farBadgeCheck }
          : { active: ICONS.fasBadgePercent, normal: ICONS.farBadgePercent },
        label: t('navigation.owner'),
        route: Path.Owner,
      },
    ],
    [t, destinationId, userDetails.data?.has_active_contracts]
  )

  const isActiveRoute = (route: Route): boolean => to(route) === location.pathname

  return (
    <Container
      sx={{
        display: variant === 'vertical' ? 'flex' : 'block',
        flexDirection: variant === 'vertical' ? 'column' : 'row',
      }}
    >
      <List disablePadding tabIndex={-1} sx={{ display: variant === 'vertical' ? 'block' : 'flex' }}>
        {navigationItems.map((item, index) => (
          <ListItem
            disablePadding
            disableGutters
            key={item.label}
            sx={{
              '&:not(:last-of-type)': {
                borderBottom: `thin solid ${COLORS.vaalea[600]}`,
              },
            }}
          >
            <NavigationButton
              id={index === 0 ? ReferenceName.SIDE_NAVIGATION : index.toString()}
              ref={index === 0 ? ref : undefined}
              LinkComponent={Link}
              onClick={() => navigate(to(item.route))}
              label={item.label}
              aria-current={isActiveRoute(item.route) ? 'page' : undefined}
              className={isActiveRoute(item.route) ? 'Mui-selected' : ''}
              icon={<FontAwesomeIcon icon={isActiveRoute(item.route) ? item.icons.active : item.icons.normal} />}
              showLabel
            />
          </ListItem>
        ))}
      </List>
    </Container>
  )
}

const Container = styled(BottomNavigation)`
  transition: none;
  width: 100%;
  height: 100%;
  background-color: ${COLORS.puuteri[300]};
`

const NavigationButton = styled(BottomNavigationAction)`
  svg {
    height: ${unit(4)};
  }
`
